// src/pages/MassSendingDetail.tsx
// src/pages/MassSendingDetail.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Button,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import ErrorIcon from '@mui/icons-material/Error';
import BlockIcon from '@mui/icons-material/Block';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

interface BroadcastDetail {
  task_id: string;
  status: 'running' | 'stopped' | 'completed';
  accounts_count: number;
  total_recipients: number;
  message_preview?: string;
  started_at: string;
  stopped_at?: string | null;
  completed_at?: string | null;
  duration?: number;
  total_sent: number;
  total_failed: number;
  progress_percentage: number;
  accounts_banned: number;
  error_breakdown: Record<string, number>;
  average_speed?: number;
  estimated_completion?: string | null;
  status_message?: string | null;
}

const MassSendingDetail: React.FC = () => {
  const { taskId } = useParams<{ taskId?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<BroadcastDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Защита от undefined
  useEffect(() => {
    if (!taskId) {
      navigate('/mass_sending');
    }
  }, [taskId, navigate]);

  const fetchDetail = async () => {
    if (!taskId) return;

    try {
      const res = await api.get(`/mass_sending/status/${taskId}`);
      setData(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Рассылка не найдена');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchDetail();
      const interval = setInterval(fetchDetail, 7000);
      return () => clearInterval(interval);
    }
  }, [taskId]);

  if (!taskId) return null;

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка статистики рассылки...</Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Рассылка не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/mass_sending')}>
          Назад к рассылкам
        </Button>
      </Container>
    );
  }

  const uptime = data.duration ? Math.floor(data.duration) : 0;
  const hours = Math.floor(uptime / 3600);
  const minutes = Math.floor((uptime % 3600) / 60);
  const seconds = uptime % 60;

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU');
  };

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/mass_sending')} sx={{ mb: 4 }}>
        Назад к рассылкам
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Рассылка #{taskId.slice(0, 8)}
        </Typography>

        <Typography color="#4B5563" fontSize="1.1rem" mb={4}>
          Аккаунтов: <strong>{data.accounts_count}</strong> · Получателей: <strong>{data.total_recipients}</strong> ·
          <AccessTimeIcon sx={{ verticalAlign: 'middle', mx: 1 }} />
          Время работы: <strong>{hours}ч {minutes}м {seconds}с</strong>
        </Typography>

        {/* Основные карточки */}
        <Box sx={{ my: 6 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#10B981">
                  {data.total_sent}
                </Typography>
                <Typography color="text.secondary">Отправлено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#DC2626">
                  {data.total_failed}
                </Typography>
                <Typography color="text.secondary">Ошибок</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#8B5CF6">
                  {data.accounts_banned}
                </Typography>
                <Typography color="text.secondary">Забанено аккаунтов</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#F59E0B">
                  {data.progress_percentage.toFixed(0)}%
                </Typography>
                <Typography color="text.secondary">Прогресс</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Скорость и прогноз */}
        {data.average_speed && (
          <Box sx={{ p: 4, bgcolor: '#EFF6FF', borderRadius: 2, mb: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Скорость и прогноз
            </Typography>
            <Typography sx={{ fontSize: '1.1rem' }}>
              Скорость: <strong>{data.average_speed.toFixed(1)} сообщений/час</strong>
              {data.estimated_completion && (
                <>
                  {' · Ожидаемое завершение: '}
                  <strong>{formatDate(data.estimated_completion)}</strong>
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* Превью сообщения */}
        {data.message_preview && (
          <Box sx={{ mb: 6, p: 5, bgcolor: '#F0FDF4', borderRadius: 3, border: '2px solid #86EFAC' }}>
            <Typography variant="h6" color="#166534" gutterBottom>
              Превью отправленного сообщения
            </Typography>
            <Box sx={{ p: 3, bgcolor: 'white', borderRadius: 2, border: '1px solid #BBF7D0' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '1.1rem', lineHeight: 1.7 }}>
                {data.message_preview}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Ошибки */}
        {Object.keys(data.error_breakdown || {}).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#DC2626" fontWeight={600} gutterBottom>
              Ошибки отправки
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(data.error_breakdown).map(([type, count]) => (
                <Chip
                  key={type}
                  icon={<ErrorIcon />}
                  label={`${type}: ${count}`}
                  color="error"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Статусное сообщение */}
        {data.status_message && (
          <Alert
            severity={data.status === 'completed' ? 'success' : data.status === 'stopped' ? 'warning' : 'info'}
            sx={{ mb: 4 }}
          >
            {data.status_message}
          </Alert>
        )}

        {/* Даты */}
        <Box sx={{ p: 4, bgcolor: '#F9FAFB', borderRadius: 2 }}>
          <Typography variant="h6" color="#1E40AF" gutterBottom>
            Временные метки
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Начало</Typography>
              <Typography fontWeight={600}>{formatDate(data.started_at)}</Typography>
            </Grid>
            {data.stopped_at && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography color="text.secondary">Остановлена</Typography>
                <Typography fontWeight={600} color="#F59E0B">
                  {formatDate(data.stopped_at)}
                </Typography>
              </Grid>
            )}
            {data.completed_at && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography color="text.secondary">Завершена</Typography>
                <Typography fontWeight={600} color="#10B981">
                  {formatDate(data.completed_at)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default MassSendingDetail;